import React, {FC, MouseEvent, useCallback, useContext, useId, useState} from 'react';
import {
    ChooseDropdown,
    DropdownV2,
    DropDownV2Context,
    Flex,
    Icon,
    PriorityIcon,
    Text
} from '@priority-software/priority-style-react';
import styles from './RouteMenu.module.scss';
import {isRTL} from '../../../../Localization';
import {ColorPicker} from '../ColorPicker';
import {ColorTile} from '../ColorPicker/ColorTile';
import {RouteType} from '../../types';
import classNames from 'classnames';
import {AdvancedDeliveryContext} from '../../reducer/context';
import {texts} from '../../texts';
import {useAvailableStatusesContext} from '../../hook/useAvailableStatusesContext';
import {StatusItem} from '../RoutesHeader';
import {updateStatus} from '../../actions/action';

type MenuItemProps = {
    label: string;
    postfixContent?: React.ReactNode;
    onClick?: () => void;
};

const MenuItem: FC<MenuItemProps> = ({label, postfixContent, onClick}) => {
    const {isOpen, openDropdown, closeDropdown} = useContext(DropDownV2Context);

    const handleToggle = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            if (isOpen) {
                closeDropdown();
            } else {
                openDropdown();
            }
        },
        [isOpen, closeDropdown, openDropdown]
    );

    return (
        <div className={styles.menuItem} onClick={e => (onClick ? onClick() : handleToggle(e))}>
            <Text
                componentClasses={styles.itemLabel}
                textColor={'primary-700'}
                skin={'paragraph-2'}
            >
                {label}
            </Text>
            {postfixContent}
        </div>
    );
};

export type RouteMenuProps = {
    portalId?: string;
    route: RouteType;
    componentClasses?: string;
    setIsScreenLocked: (isLocked: boolean) => void;
};

export const RouteMenu: FC<RouteMenuProps> = ({
    portalId,
    route,
    componentClasses,
    setIsScreenLocked
}) => {
    const {statuses: availableStatuses} = useAvailableStatusesContext();
    const [isOpenDropdown, setIsOpenDropdown] = useState(false);
    const [isOpenColorDropdown, setIsOpenColorDropdown] = useState(false);
    const isRtl = isRTL();

    const contentId = useId();

    const {routesForm} = useContext(AdvancedDeliveryContext);

    const {setRouteColor} = useContext(AdvancedDeliveryContext);

    const changeStatuses = async (status: string) => {
        setIsScreenLocked(true);
        await updateStatus(routesForm, [route], status);
        setIsScreenLocked(false);
    };

    return (
        <div className={componentClasses}>
            <DropdownV2
                isOpen={isOpenDropdown}
                onClose={() => setIsOpenDropdown(false)}
                width="fullWidth"
                contentComponentClasses={styles.dropdownContent}
                trigger={
                    <Flex
                        componentClasses={styles.threeDotsWrapper}
                        alignItems={'center'}
                        justifyContent={'center'}
                    >
                        <Icon
                            icon={PriorityIcon.threeDots}
                            hoverable
                            iconColor="primary-700"
                            onClick={() => setIsOpenDropdown(value => !value)}
                        />
                    </Flex>
                }
                openingSide={'leftSide'}
                usePortal
                portalSelector={'adpPanel'}
                zIndex={99100}
            >
                <div id={contentId}>
                    <ChooseDropdown
                        isRtl={isRtl}
                        itemBorder={false}
                        width={150}
                        onChange={({value}) => changeStatuses(value as string)}
                        noOptionsText={texts.no_options_text}
                        usePortal
                        portalSelector={contentId}
                        defaultFocusedIndex={0}
                        maxScrollHeight={320}
                        hasSearch={false}
                        searchPlaceholder={texts.search}
                        data={availableStatuses.map(status => ({
                            label: status.string1,
                            value: status.retval
                        }))}
                        itemContentRenderer={item => (
                            <StatusItem item={item} statuses={routesForm.statuses} />
                        )}
                        trigger={
                            <MenuItem
                                label={texts.change_status_to}
                                postfixContent={
                                    <Icon
                                        icon={
                                            isRtl ? PriorityIcon.arrowLeft : PriorityIcon.arrowRight
                                        }
                                    />
                                }
                            />
                        }
                    ></ChooseDropdown>
                    <DropdownV2
                        isOpen={isOpenColorDropdown}
                        onClose={() => setIsOpenColorDropdown(false)}
                        width="fullWidth"
                        contentComponentClasses={styles.colorPickerDropdown}
                        trigger={
                            <MenuItem
                                label={texts.select_route_color}
                                onClick={() => setIsOpenColorDropdown(value => !value)}
                                postfixContent={
                                    <Flex
                                        direction={'row'}
                                        justifyContent={'flex-end'}
                                        alignItems={'center'}
                                        className={classNames({
                                            [styles.rtl]: isRtl
                                        })}
                                    >
                                        <ColorTile color={route.color} width={24} height={24} />
                                        <Icon
                                            componentClasses={styles.icon}
                                            icon={
                                                isRtl
                                                    ? PriorityIcon.arrowLeft
                                                    : PriorityIcon.arrowRight
                                            }
                                        />
                                    </Flex>
                                }
                            />
                        }
                        openingSide={'leftSide'}
                        usePortal
                        portalSelector={contentId}
                    >
                        <ColorPicker
                            tileClassName={styles.tileInPicker}
                            onSelect={color => setRouteColor(Number.parseInt(route.id), color)}
                        />
                    </DropdownV2>
                </div>
            </DropdownV2>
        </div>
    );
};
